import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { ProductView } from '../../../../services/products/products.service';
import type { VariantComboGroupView } from '../../../../services/variants-combos/variants-combos.service';
import { collectComboItemsByProduct, type ComboItemTarget, type UiGroup } from '../../components/settings/options-builder/options-builder.utils';
import type { useProductCompositionEditor } from './useProductCompositionEditor';

type CompositionEditor = ReturnType<typeof useProductCompositionEditor>;

type Params = {
  employeeSession: string | null;
  isManager: boolean;
  settingsModalKey: string | null;
  activeTab: 'areas' | 'orders' | 'limit-items' | 'table-history';
  composition: CompositionEditor;
  invalidateComposition: (productId: number) => void;
};

// Architectural summary: Owns the manager-side variants/combos Options Builder drawer (open/save/close),
export function useProductOptions({
  employeeSession,
  isManager,
  settingsModalKey,
  activeTab,
  composition,
  invalidateComposition,
}: Params) {
  const { loadAllGroups, loadLimitItemsGroups, applyVariantComboGroupUpserted, applyVariantComboGroupRemoved } = composition;

  const [optionsProduct, setOptionsProduct] = useState<ProductView | null>(null);
  const [variantComboCountByProductId, setVariantComboCountByProductId] = useState<Record<number, number>>({});
  const [isCopyingGroup, setIsCopyingGroup] = useState(false);
  const [comboItemsByProductId, setComboItemsByProductId] = useState<Record<number, ComboItemTarget[]>>({});
  const [rootGroupsByProductId, setRootGroupsByProductId] = useState<Record<number, VariantComboGroupView[]>>({});
  // Root groups per product: seeded on open, then patched from SSE payloads (Rule 27), no refetch.
  const rootGroupsByProductIdRef = useRef<Record<number, VariantComboGroupView[]>>({});

  const applyRootGroups = useCallback((next: Record<number, VariantComboGroupView[]>) => {
    rootGroupsByProductIdRef.current = next;
    setRootGroupsByProductId(next);
    setComboItemsByProductId(collectComboItemsByProduct(Object.values(next).flat()));
    setVariantComboCountByProductId(
      Object.fromEntries(Object.entries(next).map(([productId, groups]) => [productId, groups.length])),
    );
  }, []);

  const reloadVariantComboCounts = useCallback(async () => {
    if (!employeeSession) {
      return;
    }
    const allGroups = isManager ? await loadAllGroups() : await loadLimitItemsGroups();
    const rootsByProduct: Record<number, VariantComboGroupView[]> = {};
    for (const group of allGroups) {
      if (group.parentItemId !== null || group.parentProductId === null) {
        continue;
      }
      (rootsByProduct[group.parentProductId] ??= []).push(group);
    }
    applyRootGroups(rootsByProduct);
  }, [employeeSession, isManager, loadAllGroups, loadLimitItemsGroups, applyRootGroups]);

  useEffect(() => {
    if (settingsModalKey === 'products' || (activeTab === 'limit-items' && !isManager)) {
      void reloadVariantComboCounts();
      return;
    }
    if (activeTab === 'limit-items' && isManager) {
      void reloadVariantComboCounts();
    }
  }, [activeTab, isManager, settingsModalKey, reloadVariantComboCounts]);

  const applyVariantComboUpsertedDelta = useCallback(
    (productId: number, group: VariantComboGroupView) => {
      invalidateComposition(productId);
      applyVariantComboGroupUpserted(productId, group);
      // Only root groups are published/tracked here; a nested group arrives inside its root's subtree.
      if (group.parentItemId !== null) {
        return;
      }
      const roots = rootGroupsByProductIdRef.current;
      const current = roots[productId] ?? [];
      const next = current.some((existing) => existing.id === group.id)
        ? current.map((existing) => (existing.id === group.id ? group : existing))
        : [...current, group];
      applyRootGroups({ ...roots, [productId]: next });
    },
    [invalidateComposition, applyVariantComboGroupUpserted, applyRootGroups],
  );
  const applyVariantComboRemovedDelta = useCallback(
    (productId: number, groupId: number) => {
      invalidateComposition(productId);
      applyVariantComboGroupRemoved(productId, groupId);
      const roots = rootGroupsByProductIdRef.current;
      const current = roots[productId];
      if (!current || !current.some((group) => group.id === groupId)) {
        return;
      }
      applyRootGroups({ ...roots, [productId]: current.filter((group) => group.id !== groupId) });
    },
    [invalidateComposition, applyVariantComboGroupRemoved, applyRootGroups],
  );

  const openProductOptions = useCallback((product: ProductView) => {
    setOptionsProduct(product);
    void composition.loadComposition(product.id);
  }, [composition]);

  const closeProductOptions = useCallback(() => {
    setOptionsProduct(null);
    composition.clearComposition();
  }, [composition]);

  const handleSaveProductOptions = useCallback(
    async (uiGroups: Parameters<typeof composition.commitDraft>[1]) => {
      if (!optionsProduct) {
        return;
      }
      try {
        await composition.commitDraft(optionsProduct.id, uiGroups);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : '';
        notifications.show({
          message: message.includes('not active tenant printers')
            ? 'לא ניתן לשמור: המדפסת שנבחרה לא זמינה'
            : 'לא ניתן לשמור את השינויים',
          color: 'red',
          autoClose: 4000,
        });
        return;
      }
      // The badge count is no longer optimistically set here — batchUpsertProductGroups publishes
      // variantComboGroup.upserted/removed for every affected group (including back to the
      // originating device), and applyVariantComboUpsertedDelta/RemovedDelta above patch the count
      // from those real events. This also avoids showing a count that hasn't actually been
      // persisted if the save is rejected.
      setOptionsProduct(null);
      composition.clearComposition();
      notifications.show({ message: 'השינויים נשמרו', autoClose: 3000 });
    },
    [composition, optionsProduct],
  );

  const copyInFlightRef = useRef(false);
  const handleCopyGroupToProducts = useCallback(
    async (group: UiGroup, targetProductIds: number[], targetItemIds: number[]) => {
      const targetCount = targetProductIds.length + targetItemIds.length;
      if (copyInFlightRef.current || targetCount === 0) {
        return;
      }
      copyInFlightRef.current = true;
      setIsCopyingGroup(true);
      try {
        await composition.copyGroupToProducts(targetProductIds, targetItemIds, group);
        notifications.show({
          message: targetCount === 1 ? 'הקבוצה הועתקה ליעד' : `הקבוצה הועתקה ל-${targetCount} יעדים`,
          autoClose: 3000,
        });
      } catch {
        notifications.show({ message: 'לא ניתן להעתיק את הקבוצה', color: 'red', autoClose: 4000 });
      } finally {
        copyInFlightRef.current = false;
        setIsCopyingGroup(false);
      }
    },
    [composition],
  );

  return {
    optionsProduct,
    variantComboCountByProductId,
    rootGroupsByProductId,
    comboItemsByProductId,
    isCopyingGroup,
    reloadVariantComboCounts,
    openProductOptions,
    closeProductOptions,
    handleSaveProductOptions,
    handleCopyGroupToProducts,
    applyVariantComboGroupUpserted: applyVariantComboUpsertedDelta,
    applyVariantComboGroupRemoved: applyVariantComboRemovedDelta,
  };
}
